import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  CheckCircle2, XCircle, AlertTriangle, Clock, Send, Radio,
  FilmIcon, MessageSquare, Trash2,
} from "lucide-react";
import { toast } from "sonner";
import DailiesViewer, { DailiesAuditDialog } from "@/pages/tv/DailiesViewer";

// Shared ledger surface: both PH owner (from /p/:id) and Show Desk
// members (from the cubicle) mount this component with the same
// projectId. Backend gates read + write by role.
//
// Props: { projectId, viewerRole = 'ph_owner' | 'desk_member' | 'admin' }
export default function EpisodeSignoffLedger({ projectId, viewerRole = "ph_owner" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("pending");
  const [submitOpen, setSubmitOpen] = useState(false);
  const [decisionCtx, setDecisionCtx] = useState(null); // {episode, action}
  const [dailiesEp, setDailiesEp]   = useState(null);   // opens DailiesViewer
  const [auditEp,   setAuditEp]     = useState(null);   // opens DailiesAuditDialog

  const load = async () => {
    setLoading(true);
    try {
      const { data: resp } = await api.get(
        `/projects/${projectId}/episode-deliveries`);
      setData(resp);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { void load(); }, [projectId]);

  const withdraw = async (id) => {
    if (!confirm("Withdraw this episode? Data is soft-deleted.")) return;
    try {
      await api.delete(`/projects/${projectId}/episode-deliveries/${id}`);
      toast.success("Withdrawn");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not withdraw");
    }
  };

  const rows = (data?.episodes || []).filter((e) => e.status === tab
    || (tab === "all"));
  const counts = data?.counts || { pending: 0, approved: 0, rejected: 0,
                                    changes_requested: 0, total: 0 };
  const isPH = ["ph_owner", "admin"].includes(viewerRole);
  const canDecide = ["desk_member", "admin"].includes(viewerRole);

  return (
    <div className="cs-card rounded-2xl p-6" data-testid="episode-ledger-panel">
      <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-white/50">
            Episode Signoff Ledger
          </div>
          <h3 className="font-display text-2xl uppercase tracking-tight mt-1">
            Deliveries · {counts.total}
          </h3>
          <div className="mt-1 text-xs text-white/55">
            {isPH
              ? "Submit finished episodes for the channel programming team to sign off."
              : "Sign off deliveries so the PH knows what's cleared for air."}
          </div>
        </div>
        {isPH && (
          <Button onClick={() => setSubmitOpen(true)}
            className="btn-themed h-11 px-5 rounded-full text-xs uppercase tracking-[0.25em]"
            data-testid="episode-submit-btn">
            <Send size={13} className="mr-1.5" /> Submit episode
          </Button>
        )}
      </div>

      {/* status counts */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <CountTile testid="ep-count-pending" label="Pending" value={counts.pending || 0}
          Icon={Clock} color="#F5B82E" />
        <CountTile testid="ep-count-approved" label="Approved" value={counts.approved || 0}
          Icon={CheckCircle2} color="#10B981" />
        <CountTile testid="ep-count-changes" label="Changes"
          value={counts.changes_requested || 0}
          Icon={AlertTriangle} color="#F97316" />
        <CountTile testid="ep-count-rejected" label="Rejected" value={counts.rejected || 0}
          Icon={XCircle} color="#EF4444" />
      </div>

      {/* Filter tabs */}
      <div className="flex items-center gap-1 border-b border-white/10 mb-5"
        data-testid="episode-tabs">
        {[
          { k: "pending",           label: "Pending" },
          { k: "changes_requested", label: "Changes" },
          { k: "approved",          label: "Approved" },
          { k: "rejected",          label: "Rejected" },
          { k: "all",               label: "All" },
        ].map(({ k, label }) => (
          <button key={k}
            onClick={() => setTab(k)}
            className={"px-3 py-2 text-[10px] tracking-[0.22em] uppercase border-b-2 -mb-px transition-colors " +
              (tab === k ? "border-[color:var(--association-theme)] text-white"
                         : "border-transparent text-white/45 hover:text-white/70")}
            data-testid={`episode-tab-${k}`}>
            {label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-sm text-white/45 py-10 text-center">Loading deliveries…</div>
      ) : rows.length === 0 ? (
        <div className="text-sm text-white/45 py-10 text-center"
          data-testid="episode-empty">
          {tab === "pending"
            ? isPH ? "Submit your first episode above." : "Nothing awaiting your signoff."
            : `No ${tab.replace("_", " ")} episodes.`}
        </div>
      ) : (
        <div className="space-y-3" data-testid="episode-list">
          {rows.map((e) => (
            <EpisodeRow key={e.id} e={e}
              isPH={isPH} canDecide={canDecide}
              onApprove={() => setDecisionCtx({ episode: e, action: "approve" })}
              onReject={() => setDecisionCtx({ episode: e, action: "reject" })}
              onChanges={() => setDecisionCtx({ episode: e, action: "request_changes" })}
              onWithdraw={() => withdraw(e.id)}
              onOpenDailies={() => setDailiesEp(e)}
              onOpenAudit={() => setAuditEp(e)} />
          ))}
        </div>
      )}

      <SubmitDialog open={submitOpen} onOpenChange={setSubmitOpen}
        projectId={projectId} onSubmitted={load} />
      <DecisionDialog ctx={decisionCtx} onClose={() => setDecisionCtx(null)}
        projectId={projectId} onDecided={load} />
      <DailiesViewer
        open={!!dailiesEp}
        onOpenChange={(v) => !v && setDailiesEp(null)}
        projectId={projectId}
        episode={dailiesEp} />
      <DailiesAuditDialog
        open={!!auditEp}
        onOpenChange={(v) => !v && setAuditEp(null)}
        projectId={projectId}
        episode={auditEp} />
    </div>
  );
}

function CountTile({ testid, label, value, Icon, color }) {
  return (
    <div className="cs-card rounded-xl p-4 relative overflow-hidden" data-testid={testid}>
      <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full opacity-15 blur-2xl"
        style={{ background: color }} />
      <div className="relative flex items-center justify-between">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center"
          style={{ background: `${color}24`, color }}>
          <Icon size={15} />
        </div>
        <div className="text-3xl font-display font-bold" style={{ color }}>
          {value}
        </div>
      </div>
      <div className="mt-3 text-[10px] tracking-[0.22em] uppercase text-white/55">
        {label}
      </div>
    </div>
  );
}

function EpisodeRow({ e, isPH, canDecide, onApprove, onReject, onChanges,
                      onWithdraw, onOpenDailies, onOpenAudit }) {
  const statusMap = {
    pending:           { bg: "bg-amber-500/15",   fg: "text-amber-300",   label: "Pending" },
    approved:          { bg: "bg-emerald-500/15", fg: "text-emerald-300", label: "Approved" },
    rejected:          { bg: "bg-red-500/15",     fg: "text-red-300",     label: "Rejected" },
    changes_requested: { bg: "bg-orange-500/15",  fg: "text-orange-300",  label: "Changes" },
  };
  const s = statusMap[e.status] || statusMap.pending;
  const decisions = e.decisions || [];

  return (
    <div className="rounded-xl p-4 bg-white/[0.03] border border-white/8 hover:bg-white/[0.05] transition-colors"
      data-testid={`episode-row-${e.id}`}>
      <div className="flex items-center gap-3 flex-wrap">
        <div className="w-10 h-10 rounded-lg bg-white/8 flex items-center justify-center text-white/75">
          <FilmIcon size={16} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-white">
              {e.season_number != null
                ? `S${e.season_number} · Ep ${e.episode_number}`
                : `Ep ${e.episode_number}`}
            </span>
            {e.title && (
              <span className="text-sm text-white/75 truncate">— {e.title}</span>
            )}
            <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.2em] ${s.bg} ${s.fg}`}
              data-testid={`episode-status-${e.status}`}>
              {s.label}
            </span>
          </div>
          <div className="mt-0.5 text-[11px] text-white/45">
            Submitted {e.delivered_at?.slice(0, 10)}
            {e.scheduled_air && ` · Air date ${e.scheduled_air}`}
            {decisions.length > 0 && ` · ${decisions.length} decision${decisions.length > 1 ? "s" : ""}`}
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          {e.files_url && (
            <button onClick={onOpenDailies}
              className="text-[10px] tracking-[0.22em] uppercase text-white/55 hover:text-white px-2"
              data-testid={`episode-files-${e.id}`}>
              Watch dailies
            </button>
          )}
          {isPH && e.files_url && (
            <button onClick={onOpenAudit}
              className="text-[10px] tracking-[0.22em] uppercase text-white/45 hover:text-white px-1"
              data-testid={`episode-audit-${e.id}`}
              aria-label="View dailies audit">
              Audit
            </button>
          )}
          {canDecide && e.status !== "approved" && (
            <>
              <Button onClick={onApprove}
                className="h-8 px-3 rounded-full text-[10px] uppercase tracking-[0.22em] bg-emerald-500/80 hover:bg-emerald-500 text-white"
                data-testid={`episode-approve-${e.id}`}>
                <CheckCircle2 size={11} className="mr-1" /> Approve
              </Button>
              <Button onClick={onChanges} variant="outline"
                className="h-8 px-3 rounded-full text-[10px] uppercase tracking-[0.22em] border-orange-500/50 text-orange-300 hover:bg-orange-500/10"
                data-testid={`episode-changes-${e.id}`}>
                Changes
              </Button>
              <Button onClick={onReject} variant="outline"
                className="h-8 px-3 rounded-full text-[10px] uppercase tracking-[0.22em] border-red-500/50 text-red-300 hover:bg-red-500/10"
                data-testid={`episode-reject-${e.id}`}>
                Reject
              </Button>
            </>
          )}
          {isPH && e.status !== "approved" && (
            <button onClick={onWithdraw}
              className="text-white/40 hover:text-red-300 transition-colors p-1.5"
              data-testid={`episode-withdraw-${e.id}`}
              aria-label="Withdraw episode">
              <Trash2 size={13} />
            </button>
          )}
        </div>
      </div>

      {/* Decision trail */}
      {decisions.length > 0 && (
        <div className="mt-3 pl-13 space-y-1.5">
          {decisions.slice(-3).reverse().map((d) => (
            <div key={d.id} className="text-[11px] text-white/60 flex items-start gap-2"
              data-testid={`decision-row-${d.id}`}>
              <MessageSquare size={10} className="mt-0.5 shrink-0 text-white/40" />
              <div>
                <b className="text-white/80">{d.decided_by_name || "Channel exec"}</b>
                <span className="text-white/45"> · {d.member_role?.replace(/_/g, " ")} · </span>
                <span className={
                  d.decision === "approve" ? "text-emerald-300"
                  : d.decision === "reject" ? "text-red-300"
                  : "text-orange-300"
                }>
                  {d.decision.replace(/_/g, " ")}
                </span>
                {d.note && <span className="text-white/70"> — {d.note}</span>}
              </div>
            </div>
          ))}
          {decisions.length > 3 && (
            <div className="text-[10px] text-white/40">
              +{decisions.length - 3} earlier decision{decisions.length - 3 > 1 ? "s" : ""}
            </div>
          )}
        </div>
      )}

      {e.delivery_note && (
        <div className="mt-2 text-[11px] text-white/50 italic">
          &ldquo;{e.delivery_note}&rdquo;
        </div>
      )}
    </div>
  );
}

function SubmitDialog({ open, onOpenChange, projectId, onSubmitted }) {
  const [epNum, setEpNum] = useState("");
  const [seasonNum, setSeasonNum] = useState("");
  const [title, setTitle] = useState("");
  const [note, setNote] = useState("");
  const [filesUrl, setFilesUrl] = useState("");
  const [air, setAir] = useState("");
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setEpNum(""); setSeasonNum(""); setTitle("");
    setNote(""); setFilesUrl(""); setAir("");
  };

  const submit = async () => {
    if (!epNum) { toast.error("Episode number is required"); return; }
    setSaving(true);
    try {
      const body = {
        episode_number: Number(epNum),
        title:          title.trim() || undefined,
        delivery_note:  note.trim()  || undefined,
        files_url:      filesUrl.trim() || undefined,
        scheduled_air:  air || undefined,
      };
      if (seasonNum) body.season_number = Number(seasonNum);
      await api.post(`/projects/${projectId}/episode-deliveries`, body);
      toast.success(`Episode ${epNum} submitted for signoff`);
      onOpenChange(false); reset(); onSubmitted?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not submit");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="bg-[#0a0a0c] border border-white/10 text-white max-w-lg"
        data-testid="episode-submit-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight text-lg">
            Submit episode for signoff
          </DialogTitle>
          <DialogDescription className="text-white/55 text-xs">
            The channel programming team gets notified via the Show Desk.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] tracking-[0.22em] uppercase text-white/55">
                Season #
              </Label>
              <Input value={seasonNum} onChange={(e) => setSeasonNum(e.target.value)}
                type="number" placeholder="Optional"
                className="mt-2 h-11 bg-black/40 border-white/15"
                data-testid="episode-season-input" />
            </div>
            <div>
              <Label className="text-[10px] tracking-[0.22em] uppercase text-white/55">
                Episode # *
              </Label>
              <Input value={epNum} onChange={(e) => setEpNum(e.target.value)}
                type="number" autoFocus
                className="mt-2 h-11 bg-black/40 border-white/15"
                data-testid="episode-number-input" />
            </div>
          </div>
          <div>
            <Label className="text-[10px] tracking-[0.22em] uppercase text-white/55">
              Episode title (optional)
            </Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Chandra confronts the past"
              className="mt-2 h-11 bg-black/40 border-white/15"
              data-testid="episode-title-input" />
          </div>
          <div>
            <Label className="text-[10px] tracking-[0.22em] uppercase text-white/55">
              Dailies / delivery link (optional)
            </Label>
            <Input value={filesUrl} onChange={(e) => setFilesUrl(e.target.value)}
              placeholder="Drive / Frame.io / S3 URL"
              className="mt-2 h-11 bg-black/40 border-white/15 font-mono text-sm"
              data-testid="episode-files-input" />
          </div>
          <div>
            <Label className="text-[10px] tracking-[0.22em] uppercase text-white/55">
              Scheduled air date (optional)
            </Label>
            <Input type="date" value={air} onChange={(e) => setAir(e.target.value)}
              className="mt-2 h-11 bg-black/40 border-white/15"
              data-testid="episode-air-input" />
          </div>
          <div>
            <Label className="text-[10px] tracking-[0.22em] uppercase text-white/55">
              Delivery note (optional)
            </Label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)}
              rows={3}
              placeholder="Anything the channel exec should notice."
              className="mt-2 bg-black/40 border-white/15 text-sm"
              data-testid="episode-note-input" />
          </div>
          <div className="flex items-center justify-end gap-2 pt-1">
            <Button variant="ghost" onClick={() => onOpenChange(false)}
              className="h-11 px-5 text-xs uppercase tracking-[0.22em]"
              data-testid="episode-submit-cancel">
              Cancel
            </Button>
            <Button onClick={submit} disabled={saving || !epNum}
              className="btn-themed h-11 px-6 rounded-full text-xs uppercase tracking-[0.25em]"
              data-testid="episode-submit-confirm">
              {saving ? "Sending…" : "Send for signoff"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DecisionDialog({ ctx, onClose, projectId, onDecided }) {
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { setNote(""); }, [ctx]);

  const submit = async () => {
    if (!ctx) return;
    setSaving(true);
    try {
      const { data } = await api.post(
        `/projects/${projectId}/episode-deliveries/${ctx.episode.id}/decision`,
        { decision: ctx.action, note: note.trim() },
      );
      toast.success(
        data.status === "approved" ? "Approved & sealed"
        : data.status === "rejected" ? "Rejected"
        : data.status === "changes_requested" ? "Changes requested"
        : "Decision logged",
      );
      onClose(); onDecided?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not decide");
    } finally {
      setSaving(false);
    }
  };

  if (!ctx) return null;
  const actionLabel = ctx.action === "approve" ? "Approve"
    : ctx.action === "reject" ? "Reject"
    : "Request changes";
  return (
    <Dialog open={!!ctx} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#0a0a0c] border border-white/10 text-white max-w-lg"
        data-testid="episode-decision-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight text-lg">
            {actionLabel} — Ep {ctx.episode.episode_number}
          </DialogTitle>
          <DialogDescription className="text-white/55 text-xs">
            {ctx.action === "approve"
              ? "Only a Programming Head's approval seals the episode. Other roles log their sign-off as a comment."
              : ctx.action === "reject"
              ? "The PH sees your rejection note — be specific so they can regroup fast."
              : "List the specific changes you need before the episode is approved."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div>
            <Label className="text-[10px] tracking-[0.22em] uppercase text-white/55">
              Note {ctx.action === "approve" ? "(optional)" : "*"}
            </Label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)}
              rows={4}
              placeholder={ctx.action === "changes"
                ? "e.g. Recut scene 3, replace BG music at 04:12."
                : ""}
              className="mt-2 bg-black/40 border-white/15 text-sm"
              data-testid="episode-decision-note" />
          </div>
          <div className="flex items-center justify-end gap-2 pt-1">
            <Button variant="ghost" onClick={onClose}
              className="h-11 px-5 text-xs uppercase tracking-[0.22em]"
              data-testid="episode-decision-cancel">
              Cancel
            </Button>
            <Button onClick={submit} disabled={saving
              || (ctx.action !== "approve" && !note.trim())}
              className={ctx.action === "approve"
                ? "btn-themed h-11 px-6 rounded-full text-xs uppercase tracking-[0.25em]"
                : "h-11 px-6 rounded-full text-xs uppercase tracking-[0.25em] " +
                  (ctx.action === "reject"
                    ? "bg-red-500/80 hover:bg-red-500 text-white"
                    : "bg-orange-500/80 hover:bg-orange-500 text-white")}
              data-testid="episode-decision-submit">
              {saving ? "Saving…" : actionLabel}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
